import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import {
  dateInputToIso,
  getAllowedWorklogDates,
  getTodayDateInputValue,
  getWorklogDateInputBounds,
  getWorklogDateRestrictionMessage,
  handleWorklogDateInputChange,
  isAllowedWorklogDate,
  parseTimeSpentInput,
  toDateInputValue,
} from "../utils/worklogUtils";
import "./LogWorkTimeModal.css";

const LogWorkTimeModal = ({
  open,
  onClose,
  taskCode = "TSK-0001-002",
  estimatedMinutes = 60,
  totalLoggedMinutes = 0,
  initialLog = null,
  saving = false,
  onSave,
}) => {
  const [timeSpent, setTimeSpent] = useState("");
  const [date, setDate] = useState(getTodayDateInputValue());
  const [description, setDescription] = useState("");
  const [timeSpentError, setTimeSpentError] = useState("");
  const [dateError, setDateError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const isEditing = Boolean(initialLog?.id);
  const dateOptions = useMemo(() => {
    const extraAllowedDates =
      isEditing && initialLog?.date ? [toDateInputValue(initialLog.date)] : [];
    return { extraAllowedDates: extraAllowedDates.filter(Boolean) };
  }, [initialLog?.date, isEditing]);

  const dateBounds = useMemo(
    () => getWorklogDateInputBounds(new Date(), dateOptions),
    [dateOptions]
  );
  const est = estimatedMinutes || 0;
  const remaining = est > 0 ? est - totalLoggedMinutes : null;
  const overBudget = remaining !== null && remaining <= 0;
  const pct = est > 0 ? Math.min(100, Math.round((totalLoggedMinutes / est) * 100)) : 0;

  const canSubmit = useMemo(() => {
    const trimmed = timeSpent.trim();
    if (!trimmed) return false;
    if (dateError) return false;
    const parsed = parseTimeSpentInput(trimmed);
    if (!parsed.valid) return false;
    return isAllowedWorklogDate(date, new Date(), dateOptions);
  }, [timeSpent, date, dateError, dateOptions]);

  useEffect(() => {
    if (!open) return;

    setTimeSpent(initialLog?.timeSpentLabel || "");
    setDate(
      toDateInputValue(initialLog?.date) ||
        getAllowedWorklogDates(new Date(), dateOptions).slice(-1)[0] ||
        getTodayDateInputValue()
    );
    setDescription(initialLog?.description || "");
    setTimeSpentError("");
    setDateError("");
    setSubmitError("");
  }, [open, initialLog, dateOptions]);

  const fmtMin = (m) => {
    if (!m) return "0m";
    const h = Math.floor(m / 60);
    const mins = m % 60;
    if (h === 0) return `${mins}m`;
    if (mins === 0) return `${h}h`;
    return `${h}h ${mins}m`;
  };

  const validateTimeSpent = (value) => {
    const parsed = parseTimeSpentInput(value);
    if (!parsed.valid) {
      setTimeSpentError(parsed.error);
      return null;
    }
    setTimeSpentError("");
    return parsed;
  };

  const handleTimeSpentChange = (event) => {
    const value = event.target.value;
    setTimeSpent(value);
    if (timeSpentError) {
      validateTimeSpent(value);
    }
  };

  const handleTimeSpentBlur = () => {
    if (timeSpent.trim()) {
      validateTimeSpent(timeSpent);
    } else {
      setTimeSpentError("");
    }
  };

  const handleDateChange = (event) => {
    const { date: nextDate, dateError: nextDateError } = handleWorklogDateInputChange(
      date,
      event.target.value,
      new Date(),
      dateOptions
    );
    setDate(nextDate);
    setDateError(nextDateError);
  };

  const handleSave = async () => {
    setSubmitError("");
    const parsed = validateTimeSpent(timeSpent);
    if (!parsed) return;

    if (!isAllowedWorklogDate(date, new Date(), dateOptions)) {
      setDateError(getWorklogDateRestrictionMessage());
      return;
    }

    const payload = {
      date: dateInputToIso(date),
      timeSpent: parsed.apiValue,
      description: description.trim(),
      timeSpentLabel: timeSpent.trim(),
    };

    try {
      await onSave?.(payload, initialLog);
    } catch (err) {
      setSubmitError(err?.message || "Failed to save work log.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          padding: 0,
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        <div className="lwt-modal-root">
          <div className="lwt-header">
            <div>
              <h2 className="lwt-title">{isEditing ? "Edit Work Log" : "Log Work Time"}</h2>
              <p className="lwt-subtitle">{taskCode}</p>
            </div>
            <button className="lwt-close-btn" onClick={onClose} disabled={saving}>
              ✕
            </button>
          </div>

          <div className="lwt-summary-section">
            <div className="lwt-summary-row">
              <div className="lwt-summary-col">
                <span className="lwt-summary-label">LOGGED</span>
                <span className="lwt-summary-value">{fmtMin(totalLoggedMinutes)}</span>
              </div>
              <div className="lwt-summary-col lwt-center">
                <span className="lwt-summary-label">ESTIMATED</span>
                <span className="lwt-summary-value">{est ? fmtMin(est) : "—"}</span>
              </div>
              <div className="lwt-summary-col lwt-right">
                <span className="lwt-summary-label">REMAINING</span>
                <span className={`lwt-summary-value${overBudget ? " lwt-over" : ""}`}>
                  {remaining === null ? "—" : overBudget ? "Over" : fmtMin(remaining)}
                </span>
              </div>
            </div>

            <div className="lwt-progress-track">
              <div
                className={`lwt-progress-bar${overBudget ? " lwt-progress-bar--over" : ""}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="lwt-progress-hint">
              Original estimate: {est ? fmtMin(est) : "—"} - {pct}% used
            </p>
          </div>

          <div className="lwt-form-section">
            {submitError && <p className="lwt-error-banner">{submitError}</p>}

            <div className="lwt-form-group">
              <label className="lwt-label">
                Time Spent <span className="lwt-required">*</span>
              </label>
              <input
                type="text"
                className={`lwt-input${timeSpentError ? " lwt-input--error" : ""}`}
                placeholder="e.g. 2d · 1w · 2h · 30min · 1h 30min"
                value={timeSpent}
                onChange={handleTimeSpentChange}
                onBlur={handleTimeSpentBlur}
                disabled={saving}
              />
              {timeSpentError ? (
                <p className="lwt-error-text">{timeSpentError}</p>
              ) : (
                <p className="lwt-help-text">
                  Accepted formats: 2d · 1w · 3m · 1y · 2h · 30min · 1h 30min
                </p>
              )}
            </div>

            <div className="lwt-form-group">
              <label className="lwt-label">Date</label>
              <input
                type="date"
                className={`lwt-input${dateError ? " lwt-input--error" : ""}`}
                value={date}
                min={dateBounds.min}
                max={dateBounds.max}
                onChange={handleDateChange}
                disabled={saving}
              />
              {dateError ? (
                <p className="lwt-error-text">{dateError}</p>
              ) : (
                <p className="lwt-help-text">{getWorklogDateRestrictionMessage()}</p>
              )}
            </div>

            <div className="lwt-form-group">
              <label className="lwt-label">Description</label>
              <textarea
                className="lwt-input lwt-textarea"
                placeholder="What did you work on?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={saving}
              />
            </div>
          </div>

          <div className="lwt-footer">
            <button className="lwt-btn-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              className="lwt-btn-save"
              onClick={handleSave}
              disabled={saving || !canSubmit}
            >
              {saving ? "Saving..." : isEditing ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LogWorkTimeModal;
